<template>
  <v-dialog :value="dialogProp.show" persistent max-width="800">
    <v-card class="elevation-12">
      <v-toolbar :dark="$vuetify.theme.dark" color="primary">
        <v-toolbar-title>
          {{ `${$t('Просмотр запроса по торговой процедуре')} (${$t(request.requestType
            ? LOT_REQUEST_TYPE_ENUM[request.requestType]
            : null)})` }}
        </v-toolbar-title>
      </v-toolbar>
      <v-row v-if="!!generalError || isLoading" class="my-0 py-0 mx-1">
        <v-col cols="12">
          <v-progress-linear :active="isLoading" color="loader" indeterminate />
        </v-col>
        <v-col class="mx-1">
          <general-error v-if="!!generalError" />
        </v-col>
      </v-row>
      <v-card-text class="py-2">
        <v-expansion-panels>
          <v-expansion-panel v-test="{ panel: '' }">
            <v-expansion-panel-header>
              <div>
                <v-icon>
                  info
                </v-icon>
                {{ $t('Общая информация о запросе') }}
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content eager>
              <v-row class="text-left px-4">
                <text-view
                    multiline
                    md="6"
                    :display="request.requestType
                      ? $t(LOT_REQUEST_TYPE_ENUM[request.requestType])
                      : null"
                    :label="$t('Тип запроса')" />
                <text-view
                    multiline
                    md="6"
                    :display="request.status
                      ? $t(LOT_REQUEST_STATUS_ENUM[request.status])
                      : null"
                    :label="$t('Статус запроса')" />
                <date-view
                    multiline
                    md="6"
                    :display="request.requestDate"
                    :label="$t('Дата подачи запроса')"
                    date-format="DD.MM.YYYY HH:mm" />
                <date-view
                    v-if="!!request.processDate"
                    multiline
                    md="6"
                    :display="request.processDate"
                    :label="$t('Дата обработки запроса')"
                    date-format="DD.MM.YYYY HH:mm" />
                <text-view
                    v-if="!!request.processDate"
                    multiline
                    md="6"
                    :display="request.processComment
                      ? request.processComment
                      : null"
                    :label="$t('Комментарий администратора')" />
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel v-test="{ panel: '' }">
            <v-expansion-panel-header>
              <div>
                <v-icon>
                  attach_file
                </v-icon>
                {{ $t('Прикрепленные документы') }}
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content eager>
              <v-row>
                <v-col class="my-2">
                  <v-data-table
                      :headers="headers"
                      :items="documents"
                      :items-per-page="itemsPerPage"
                      item-key="fileId"
                      disable-sort-by
                      dense
                      :locale="systemLocale">
                    <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
                    <template v-slot:item.documentType="{ item }">
                      {{ $t(DOCUMENT_TYPE_ENUM[item.documentType]) }}
                    </template>
                    <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
                    <template v-slot:item.fileName="{ item }">
                      {{ item.fileName }}
                    </template>
                    <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
                    <template v-slot:item.fileId="{ item }">
                      <btn-icon-tip
                          :tip="$t('Скачать документ')"
                          icon="get_app"
                          @click="getBusinessFile(item.fileId)" />
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
            id="cancelBtn"
            v-test="{ btn: 'cancel' }"
            color="primary"
            @click.stop="cancel">
          {{ $t('Закрыть') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import TextView from '@/components/reused/ui/TextView.vue'
  import DateView from '@/components/reused/ui/DateView.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import getFileMixin from '@/utils/mixins/scoped/getFileMixin.js'
  import { DOCUMENT_TYPE_ENUM, LOT_REQUEST_TYPE_ENUM, LOT_REQUEST_STATUS_ENUM } from '@/constants/enums'
  import { mapActions, mapState } from 'vuex'
  import { LOT_REQUESTS_MANAGEMENT } from '@/constants/store.js'

  export default {
    components: {
      BtnIconTip,
      TextView,
      DateView,
      GeneralError
    },
    mixins: [
      getFileMixin('isLoading')
    ],
    props: {
      dialogProp: {
        type: Object,
        default: () => {
          return {
            lotId: null,
            requestId: null,
            show: false
          }
        }
      }
    },
    data () {
      return {
        DOCUMENT_TYPE_ENUM,
        LOT_REQUEST_TYPE_ENUM,
        LOT_REQUEST_STATUS_ENUM,
        isLoading: false,
        itemsPerPage: 5
      }
    },
    computed: {
      ...mapState(LOT_REQUESTS_MANAGEMENT.NAMESPACE, {
        request: state => (state[LOT_REQUESTS_MANAGEMENT.STATE.CURRENT_REQUEST] || {})
      }),
      headers () {
        return [
          { text: this.$t('Документ'), align: 'start', sortable: true, value: 'documentType' },
          { text: this.$t('Название файла'), align: 'start', sortable: true, value: 'fileName' },
          { text: this.$t('Действия'), align: 'start', sortable: true, value: 'fileId' }
        ]
      },
      documents () {
        return this.request.documents || []
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        vm.getLotRequestInfo()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(LOT_REQUESTS_MANAGEMENT.NAMESPACE, {
        getLotRequestInfoAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.GET_LOT_REQUEST_INFO,
        clearLotRequestInfoAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.CLEAR_LOT_REQUEST_INFO
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearLotRequestInfoAction()
          ])
      },
      getLotRequestInfo: async function () {
        try {
          this.isLoading = true
          await this.getLotRequestInfoAction({
            params: this.params,
            lotId: this.dialogProp.lotId,
            lotType: this.dialogProp.lotType,
            requestId: this.dialogProp.requestId
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      cancel: function () {
        this.$emit('close')
      }
    }
  }
</script>
